import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type FocusEvent, type PointerEvent as RPointerEvent } from 'react';
import { EnlaceExterno } from './enlaces';
import { createPortal } from 'react-dom';
import type { Components } from 'react-markdown';

/**
 * Procedencia por dato (2026-10-19). El sistema (no el modelo) asigna ids F1..Fn a las fuentes
 * del borrador y el markdown trae marcas `[F3]` (varias seguidas `[F3][F7]`) o `[F3?]` cuando la
 * marca no pudo verificarse. En la UI cada marca se vuelve un superíndice numerado con una burbuja
 * que muestra la fuente registrada (nombre, ruta/URL, fecha de consulta y extracto), para que
 * cualquiera pueda auditar el capítulo sin abrir el Word.
 *
 *  - `conMarcas(md)` preprocesa el markdown: `[F3]` → `[3](#fuente-F3)`, `[F3?]` → `[3?](#fuente-F3)`.
 *  - `componentesMarcas(fuentes)` entrega el `components={{ a }}` de react-markdown v10: los
 *    enlaces `#fuente-Fn` se pintan como <Marca/>; el resto sigue siendo un enlace normal.
 *  - `ListaFuentes` pinta el `details` "Fuentes consultadas (N)" con id · nombre · enlace/ruta · fecha.
 */
export interface FuenteProcedencia {
  id?: string;
  tipo?: 'documento' | 'url' | 'seia' | 'catastro' | 'ficha';
  ruta: string;
  nombre: string;
  url?: string | null;
  fechaConsulta?: string | null;
  extracto?: string;
  usada?: boolean;
}

const RE_MARCA = /\[F(\d+)(\?)?\]/g;
const PREFIJO = '#fuente-';

/** Reemplaza cada marca `[Fn]` / `[Fn?]` por un enlace interno `[n](#fuente-Fn)` / `[n?](#fuente-Fn)`. */
export function conMarcas(md: string | null | undefined): string {
  if (!md) return '';
  return md
    .replace(/[ \t]+(?=\[F\d+\??\])/g, '') // el superíndice va pegado a la palabra, como una cita
    .replace(RE_MARCA, (_m, n: string, duda?: string) => `[${n}${duda ?? ''}](${PREFIJO}F${n})`);
}

/** `true` si el markdown trae al menos una marca de procedencia. */
export function tieneMarcas(md: string | null | undefined): boolean {
  return !!md && /\[F\d+\??\]/.test(md);
}

/** ISO (fecha o timestamp) → dd/mm/aaaa. Vacío si no se puede interpretar. */
export function fechaCorta(iso?: string | null): string {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'America/Santiago' });
}

const MAX_EXTRACTO = 150; // ~3 líneas en la burbuja de 300px
function recortar(texto?: string): string {
  const t = (texto ?? '').replace(/\s+/g, ' ').trim();
  if (!t) return '';
  return t.length > MAX_EXTRACTO ? `${t.slice(0, MAX_EXTRACTO).trimEnd()}…` : t;
}

function esUrl(s?: string | null): s is string {
  return !!s && /^https?:\/\//i.test(s);
}

export function buscarFuente(fuentes: FuenteProcedencia[] | null | undefined, id: string): FuenteProcedencia | undefined {
  return (fuentes ?? []).find((f) => f.id === id);
}

const NO_VERIFICADA = 'Fuente no verificada por el sistema';

/**
 * Superíndice de procedencia con burbuja. Mismo patrón accesible que `Ayuda`: desktop hover y
 * foco de teclado; móvil tap abre, tap fuera / mismo tap / Escape / scroll cierran. La burbuja
 * va en un portal `position: fixed` acotado al viewport y, a diferencia de Ayuda, es interactiva
 * (el enlace a la URL de la fuente se puede tocar) y no se cierra al tocar dentro de ella.
 */
export function Marca({ id, dudosa, fuente }: { id: string; dudosa: boolean; fuente?: FuenteProcedencia }) {
  const numero = id.replace(/^F/, '');
  const verificada = !!fuente && !dudosa;
  const descId = useId();
  const ancla = useRef<HTMLSpanElement>(null);
  const burbujaRef = useRef<HTMLSpanElement>(null);
  const salida = useRef<number | null>(null);
  const [abierto, setAbierto] = useState(false);
  const [hover, setHover] = useState(false);
  const [foco, setFoco] = useState(false);
  const [pos, setPos] = useState<{ left: number; top?: number; bottom?: number; ancho: number; flecha: number; arriba: boolean } | null>(null);
  const visible = abierto || hover || foco;

  const calcular = useCallback(() => {
    const r = ancla.current?.getBoundingClientRect();
    if (!r) return;
    const margen = 12;
    const ancho = Math.min(300, window.innerWidth - margen * 2);
    const centro = r.left + r.width / 2;
    const left = Math.max(margen, Math.min(centro - ancho / 2, window.innerWidth - margen - ancho));
    const arriba = r.top > 200;
    setPos({
      left,
      ancho,
      flecha: Math.max(14, Math.min(centro - left, ancho - 14)),
      arriba,
      ...(arriba ? { bottom: window.innerHeight - r.top + 8 } : { top: r.bottom + 8 }),
    });
  }, []);

  useLayoutEffect(() => { if (visible) calcular(); }, [visible, calcular]);

  useEffect(() => {
    if (!visible) return;
    const cerrar = () => { setAbierto(false); setHover(false); setFoco(false); };
    const fuera = (e: PointerEvent) => {
      const t = e.target as Node;
      if (!ancla.current?.contains(t) && !burbujaRef.current?.contains(t)) cerrar();
    };
    const tecla = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); cerrar(); } };
    document.addEventListener('pointerdown', fuera);
    document.addEventListener('keydown', tecla);
    document.addEventListener('scroll', cerrar, true);
    window.addEventListener('resize', cerrar);
    return () => {
      document.removeEventListener('pointerdown', fuera);
      document.removeEventListener('keydown', tecla);
      document.removeEventListener('scroll', cerrar, true);
      window.removeEventListener('resize', cerrar);
    };
  }, [visible]);

  // Hover solo con mouse real; al salir se espera un instante para poder entrar a la burbuja.
  const entrar = (e: RPointerEvent) => {
    if (e.pointerType !== 'mouse') return;
    if (salida.current) { window.clearTimeout(salida.current); salida.current = null; }
    setHover(true);
  };
  const salir = (e: RPointerEvent) => {
    if (e.pointerType !== 'mouse') return;
    salida.current = window.setTimeout(() => setHover(false), 140);
  };

  const fecha = fechaCorta(fuente?.fechaConsulta);
  const extracto = recortar(fuente?.extracto);
  const destino = fuente ? (esUrl(fuente.url) ? fuente.url : esUrl(fuente.ruta) ? fuente.ruta : null) : null;
  const srTexto = fuente
    ? `Fuente ${numero}: ${fuente.nombre}. ${destino ?? fuente.ruta}.${fecha ? ` Consultada el ${fecha}.` : ''}${verificada ? '' : ` ${NO_VERIFICADA}.`}`
    : `Fuente ${numero}: ${NO_VERIFICADA}.`;

  const burbuja = visible && pos
    ? createPortal(
        <span
          ref={burbujaRef}
          role="tooltip"
          aria-hidden="true"
          onPointerEnter={entrar}
          onPointerLeave={salir}
          style={{ left: pos.left, top: pos.top, bottom: pos.bottom, width: pos.ancho }}
          className="anim-entrada fixed z-[60] block rounded-xl bg-azul-noche px-3 py-2.5 text-left text-[11.5px] font-normal leading-relaxed text-white shadow-xl ring-1 ring-black/10"
        >
          <span className="flex items-center gap-1.5">
            <span className={`inline-flex h-4 min-w-4 items-center justify-center rounded px-1 text-[10px] font-medium ${verificada ? 'bg-white/15 text-celeste' : 'bg-amber-400/20 text-amber-300'}`}>{id}</span>
            <span className="font-medium text-white">{fuente ? fuente.nombre : NO_VERIFICADA}</span>
          </span>
          {fuente && (
            <span className="mt-1 block break-all text-[11px] text-white/80">
              {destino ? (
                <a href={destino} target="_blank" rel="noopener noreferrer" className="underline decoration-white/40 underline-offset-2 hover:text-celeste">{destino}</a>
              ) : fuente.ruta}
            </span>
          )}
          {fuente && fecha && <span className="mt-0.5 block text-[11px] text-white/70">Consultada el {fecha}</span>}
          {fuente && !verificada && <span className="mt-1 block text-[11px] text-amber-300">{NO_VERIFICADA}</span>}
          {fuente && extracto && (
            <span className="mt-1.5 block border-l-2 border-white/25 pl-2 italic text-white/85">
              «{extracto}»
            </span>
          )}
          <span
            aria-hidden="true"
            style={{ left: pos.flecha }}
            className={`absolute h-2 w-2 -translate-x-1/2 rotate-45 bg-azul-noche ${pos.arriba ? 'top-full -translate-y-1' : 'bottom-full translate-y-1'}`}
          />
        </span>,
        document.body,
      )
    : null;

  return (
    <span ref={ancla} className="marca-fuente inline-flex align-baseline" onPointerEnter={entrar} onPointerLeave={salir}>
      <button
        type="button"
        aria-label={`Fuente ${numero}${verificada ? '' : ' (no verificada)'}`}
        aria-describedby={descId}
        aria-expanded={visible}
        onClick={() => setAbierto((v) => !v)}
        onFocus={(e: FocusEvent) => { if ((e.target as HTMLElement).matches(':focus-visible')) setFoco(true); }}
        onBlur={() => setFoco(false)}
        className={`-my-0.5 ml-px inline-flex h-4 min-w-[1.05em] cursor-pointer items-center justify-center rounded px-[2px] align-super text-[0.68em] font-medium leading-none transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-azul-profundo ${
          verificada
            ? `text-azul-profundo hover:bg-celeste/25 ${visible ? 'bg-celeste/25' : ''}`
            : `text-amber-600 hover:bg-amber-100 ${visible ? 'bg-amber-100' : ''}`
        }`}
      >
        {numero}{verificada ? '' : '?'}
      </button>
      <span id={descId} className="sr-only">{srTexto}</span>
      {burbuja}
    </span>
  );
}

/** `components` para react-markdown v10: `#fuente-Fn` → <Marca/>; otros enlaces, en pestaña nueva (EnlaceExterno). */
export function componentesMarcas(fuentes: FuenteProcedencia[] | null | undefined): Pick<Components, 'a'> {
  return {
    a: ({ node: _node, href, children, ...rest }) => {
      if (typeof href === 'string' && href.startsWith(PREFIJO)) {
        const id = href.slice(PREFIJO.length);
        const texto = Array.isArray(children) ? children.join('') : String(children ?? '');
        return <Marca id={id} dudosa={texto.endsWith('?')} fuente={buscarFuente(fuentes, id)} />;
      }
      void rest;
      return <EnlaceExterno href={href}>{children}</EnlaceExterno>;
    },
  };
}

const ICONO_TIPO: Record<string, string> = { documento: '📄', url: '🔗', seia: '🏛️', catastro: '🗺️', ficha: '📋' };

/** Lista colapsable "Fuentes consultadas (N)": id · nombre · enlace/ruta · fecha; las no citadas en gris. */
export function ListaFuentes({ fuentes, className = '' }: { fuentes: FuenteProcedencia[] | null | undefined; className?: string }) {
  if (!fuentes || fuentes.length === 0) return null;
  return (
    <details className={`rounded-lg border border-slate-200 bg-[#FAFBFC] px-3 py-1.5 ${className}`}>
      <summary className="cursor-pointer text-[11px] font-medium text-azul-profundo">Fuentes consultadas ({fuentes.length})</summary>
      <ul className="mt-1.5 flex flex-col gap-1">
        {fuentes.map((f, i) => {
          const noCitada = f.usada === false;
          const destino = esUrl(f.url) ? f.url : esUrl(f.ruta) ? f.ruta : null;
          const fecha = fechaCorta(f.fechaConsulta);
          return (
            <li key={f.id ?? `${f.ruta}-${i}`} className={`flex min-w-0 items-start gap-1.5 text-[11px] font-light leading-snug ${noCitada ? 'text-gris/60' : 'text-gris'}`}>
              <span className={`mt-px inline-flex h-4 shrink-0 items-center rounded px-1 font-mono text-[10px] font-medium ${noCitada ? 'bg-slate-100 text-gris/60' : 'bg-celeste/25 text-azul-profundo'}`}>
                {f.id ?? ICONO_TIPO[f.tipo ?? 'documento'] ?? '📄'}
              </span>
              <span className="min-w-0 flex-1 break-words">
                <span className={`font-normal ${noCitada ? 'text-gris/70' : 'text-azul-noche'}`}>{f.nombre}</span>
                {noCitada && <span className="ml-1 text-gris/60">(no citada)</span>}
                <span className="flex min-w-0 items-baseline gap-1">
                  <span className="min-w-0 truncate" title={destino ?? f.ruta}>
                    {destino
                      ? <a href={destino} target="_blank" rel="noopener noreferrer" className={`underline underline-offset-2 ${noCitada ? 'hover:text-gris' : 'hover:text-azul-profundo'}`}>{destino}</a>
                      : f.ruta}
                  </span>
                  {fecha && <span className="shrink-0 whitespace-nowrap">· {fecha}</span>}
                </span>
              </span>
            </li>
          );
        })}
      </ul>
    </details>
  );
}

export const AYUDA_PROCEDENCIA =
  "Cada dato lleva una marca numerada que apunta a su fuente registrada por el sistema (no por el modelo). Así cualquiera puede auditar el capítulo y Andrés puede explicar de dónde salió cada cifra. En el Word las marcas van ocultas, salvo que actives 'Mostrar fuentes' en Formato Documentos.";
